import withValidDatasourceCheck from 'lib/enhancers/withValidDatasourceCheck';
import { Field, Item, LinkField, Text, getFieldValue, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import React from 'react';

import compose from 'lib/enhancers/compose';
import Link from 'components/helpers/Link/Link';

import type { ComponentProps } from 'lib/component-props';
import withStyles from 'lib/enhancers/withStyles';
import styles from './AnnouncementListing.module.scss';
import { RecordOfFields } from 'models/fields';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';
import { removeLineBreaks, getEffectiveMonthandYear, getLanguageBasedUrl } from 'lib/utils';

interface Label {
  id: string;
  value: string;
}

interface Announcement {
  announcementTitle: string;
  announcementSummary: string;
  announcementLink: string;
  effectiveDate: string;
  labels: Label[];
  url: string;
  id: string;
}

type AnnouncementListingProps = ComponentProps & {
  fields: {
    featuredAnnouncementTitle: Field<string>;
    featuredAnnouncementTitleHeadingLevel: Item | null;
    featuredAnnouncementDescription: Field<string>;
    recentAnnouncementTitle: Field<string>;
    recentAnnouncementTitleHeadingLevel: Item | null;
    recentAnnouncementDescription: Field<string>;
    announcementItemTitleHeadingLevel: Item | null;
    announcementListingLinkText: Field<string>;
    announcementListingLinkDestination: LinkField;
    featuredAnnouncements: Announcement[];
    recentAnnouncements: Announcement[];
  };
};

const cx = classNames.bind(styles);

const AnnouncementListing = (props: AnnouncementListingProps): React.JSX.Element => {
  const { className, fields } = props;
  const { sitecoreContext } = useSitecoreContext();

  if (!sitecoreContext.pageEditing && !fields) {
    return <></>;
  }

  const {
    featuredAnnouncements,
    recentAnnouncements,
    announcementItemTitleHeadingLevel,
    recentAnnouncementTitleHeadingLevel,
    recentAnnouncementTitle,
    featuredAnnouncementTitleHeadingLevel,
    featuredAnnouncementTitle,
    announcementListingLinkDestination,
    announcementListingLinkText,
  } = fields;

  const announcementItemTitleHeadingLevelItemField = announcementItemTitleHeadingLevel?.fields as RecordOfFields;
  const announcementItemTitleHeadingLevelValue =
    getFieldValue<string>(announcementItemTitleHeadingLevelItemField, 'Value') || 'h3';
  const featuredAnnouncementTitleHeadingLevelItemField =
    featuredAnnouncementTitleHeadingLevel?.fields as RecordOfFields;
  const featuredAnnouncementTitleHeadingLevelValue =
    getFieldValue<string>(featuredAnnouncementTitleHeadingLevelItemField, 'Value') || 'h2';
  const recentAnnouncementTitleHeadingLevelItemField = recentAnnouncementTitleHeadingLevel?.fields as RecordOfFields;
  const recentAnnouncementTitleHeadingLevelValue =
    getFieldValue<string>(recentAnnouncementTitleHeadingLevelItemField, 'Value') || 'h2';

  return (
    <div className={cx('announcement-listing', 'component-container-edge', className)}>
      {featuredAnnouncements && featuredAnnouncements?.length > 0 && (
        <div>
          <Text
            field={featuredAnnouncementTitle}
            className="font-style-xl"
            tag={featuredAnnouncementTitleHeadingLevelValue}
            encode={sitecoreContext.pageEditing}
          />
          <RichTextWrapper field={fields?.featuredAnnouncementDescription} className={cx('body')} />
          {featuredAnnouncements?.map((item: Announcement, index: number) => {
            const effectiveDate = item.effectiveDate;
            return (
              <div key={index} className={cx('announcement-listing-card')}>
                <a href={getLanguageBasedUrl(item.url, sitecoreContext.language)} className={cx('announcement-link')}>
                  <Text
                    field={{ value: item.announcementTitle }}
                    className="text-purple-50 mb-2 mt-4 font-style-lg"
                    tag={announcementItemTitleHeadingLevelValue}
                    encode={sitecoreContext.pageEditing}
                  />
                </a>

                <Text
                  field={{ value: removeLineBreaks(item.announcementSummary) }}
                  encode={sitecoreContext.pageEditing}
                />

                <div className={cx('announcement-date-label')}>
                  {effectiveDate && isNaN(Date.parse(effectiveDate)) && (
                    <strong>
                      {getEffectiveMonthandYear(effectiveDate).month} {getEffectiveMonthandYear(effectiveDate).year}
                    </strong>
                  )}
                  {item.labels?.map((labelItem, index) => (
                    <span key={index}>{labelItem.value}</span>
                  ))}
                </div>
                <hr />
              </div>
            );
          })}
        </div>
      )}

      {recentAnnouncements && recentAnnouncements?.length > 0 && (
        <div>
          <Text
            field={recentAnnouncementTitle}
            className="font-style-xl"
            tag={recentAnnouncementTitleHeadingLevelValue}
            encode={sitecoreContext.pageEditing}
          />
          <RichTextWrapper field={fields?.recentAnnouncementDescription} className={cx('body')} />
          {recentAnnouncements?.map((item: Announcement, index: number) => {
            const effectiveDate = item.effectiveDate;
            return (
              <div key={index} className={cx('announcement-listing-card')}>
                <a href={getLanguageBasedUrl(item.url, sitecoreContext.language)} className={cx('announcement-link')}>
                  <Text
                    field={{ value: item.announcementTitle }}
                    className="text-purple-50 mb-2 mt-4 font-style-lg"
                    tag={announcementItemTitleHeadingLevelValue}
                    encode={sitecoreContext.pageEditing}
                  />
                </a>

                <Text
                  field={{ value: removeLineBreaks(item.announcementSummary) }}
                  encode={sitecoreContext.pageEditing}
                />

                <div className={cx('announcement-date-label')}>
                  {effectiveDate && isNaN(Date.parse(effectiveDate)) && (
                    <strong>
                      {getEffectiveMonthandYear(effectiveDate).month} {getEffectiveMonthandYear(effectiveDate).year}
                    </strong>
                  )}
                  {item.labels?.map((labelItem, index) => (
                    <span key={index}>{labelItem.value}</span>
                  ))}
                </div>
                <hr />
              </div>
            );
          })}
        </div>
      )}
      {announcementListingLinkDestination?.value.href !== '' && (
        <Link field={announcementListingLinkDestination} icon="carat" showLinkTextWithChildrenPresent={false}>
          <Text field={announcementListingLinkText} encode={sitecoreContext.pageEditing} />
        </Link>
      )}
    </div>
  );
};

export default compose<AnnouncementListingProps>(withValidDatasourceCheck(), withStyles())(AnnouncementListing);
